// CumulativePositionBar — the contract's consumed-versus-remaining position on
// an INVOICE basis: what has been approved, what has been billed and is
// waiting on a decision, and what is left of the contract value.
//
// Shared by the summary's financial card and the financial performance
// report, so the two surfaces cannot draw the same position differently.
//
// The bar is sized against what it has to fit — approved plus awaiting plus
// remaining — which the server's identity makes the contract value plus any
// overrun. Sizing it against the contract value alone looks right until
// billing passes that value: the segments then sum past the whole and the
// track silently squeezes them back in, so a contract billed three times over
// draws as an ordinary full bar. Widening the track instead keeps the
// proportions honest and leaves room for a rule marking where the contract
// value ran out.
//
// "Approved", never "Paid": invoice statuses stop at approved and the system
// holds no settlement record.

import StackedBarBreakdown from "../tracking/shared/StackedBarBreakdown";
import { formatPercent } from "./reportFormat";
import { formatAud2dp } from "../../../utils/formatCurrency";
import type {
  FinancialCumulative,
  FinancialReportModel,
} from "../../../types/report";

export interface CumulativePositionBarProps {
  cumulative: FinancialCumulative;
  gstBasis: FinancialReportModel["gstBasis"];
  /** Test hooks. The two surfaces that render this bar were shipped with
   *  their own ids and their specs read them, so the ids are passed in
   *  rather than derived from one prefix. */
  barTestId: string;
  paceTestId: string;
  overrunTestId: string;
}

export default function CumulativePositionBar({
  cumulative,
  gstBasis,
  barTestId,
  paceTestId,
  overrunTestId,
}: CumulativePositionBarProps) {
  const {
    approved,
    invoicedNotApproved,
    remaining,
    overrun,
    contractValue,
    termElapsedPct,
    valueConsumedPct,
  } = cumulative;

  const trackTotal = approved + invoicedNotApproved + remaining;
  const overBudget = overrun > 0;

  return (
    <div>
      <StackedBarBreakdown
        testId={barTestId}
        total={trackTotal}
        segments={[
          {
            key: "approved",
            label: "Approved",
            value: approved,
            colorClass: "bg-emerald-500 dark:bg-emerald-400",
            formattedValue: formatAud2dp(approved),
          },
          {
            key: "awaiting",
            label: "Invoiced, awaiting approval",
            value: invoicedNotApproved,
            colorClass: "bg-amber-500 dark:bg-amber-400",
            formattedValue: formatAud2dp(invoicedNotApproved),
          },
          {
            key: "remaining",
            label: "Remaining",
            value: remaining,
            colorClass: "bg-slate-300 dark:bg-slate-600",
            formattedValue: formatAud2dp(remaining),
          },
        ]}
        // An overrun is the only way the track can be wider than the contract
        // value, so the mark is drawn exactly when there is something past it
        // to see. `trackTotal` is positive whenever it is.
        limitMarker={
          overBudget
            ? {
                atPct: (contractValue / trackTotal) * 100,
                label: `Contract value ${formatAud2dp(contractValue)}`,
                colorClass: "bg-red-600 dark:bg-red-400",
              }
            : undefined
        }
      />
      <p
        data-testid={paceTestId}
        className="mt-2 text-caption tabular-nums text-slate-500 dark:text-slate-400"
      >
        {formatPercent(termElapsedPct)} of the term elapsed{" "}
        <span aria-hidden="true">·</span>{" "}
        {formatPercent(valueConsumedPct)} of the value consumed
      </p>
      {overBudget && (
        <p
          data-testid={overrunTestId}
          className="mt-1 text-caption font-medium tabular-nums text-red-600 dark:text-red-400"
        >
          Billing exceeds the contract value by {formatAud2dp(overrun)}{" "}
          {gstBasis}.
        </p>
      )}
    </div>
  );
}
